import { type PackageDetail } from '@utazzo/shared';
import { UzAlert, UzButton, UzCombobox } from '@utazzo/ui';
import { Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSetPackageCollections } from '../use-packages';

import {
  useCreateMarketingTag,
  useMarketingTagsList,
} from '@/features/marketing-tags/use-marketing-tags';

interface SelectedTag {
  marketingTagId: string;
  name: string;
}

const THEMES_QUERY = {
  page: 1,
  pageSize: 200,
  sortBy: 'name',
  sortDir: 'asc' as const,
  type: 'theme',
};

const COLLECTIONS_QUERY = {
  page: 1,
  pageSize: 200,
  sortBy: 'name',
  sortDir: 'asc' as const,
  type: 'collection',
};

const seedTags = (
  collections: PackageDetail['collections'],
  masterIds: Set<string>,
): SelectedTag[] =>
  collections
    .filter((c) => masterIds.has(c.marketingTagId))
    .map((c) => ({ marketingTagId: c.marketingTagId, name: c.name }));

interface CollectionsEditorProps {
  pkg?: PackageDetail | null;
  /** Controlled mode: the combined marketingTagId[] (themes + collections). */
  draftValue?: string[];
  onDraftChange?: (ids: string[]) => void;
  /** Extra classes for the root (e.g. grid column span). */
  className?: string;
}

export const CollectionsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
  className,
}: CollectionsEditorProps): React.JSX.Element => {
  const isControlled = onDraftChange !== undefined;

  const themesQuery = useMarketingTagsList(THEMES_QUERY);
  const collectionsQuery = useMarketingTagsList(COLLECTIONS_QUERY);
  // Always call unconditionally; fall back to '' in controlled mode.
  const set = useSetPackageCollections(pkg?.id ?? '');
  const createTag = useCreateMarketingTag();

  const themes = useMemo(() => themesQuery.data?.data ?? [], [themesQuery.data]);
  const collections = useMemo(() => collectionsQuery.data?.data ?? [], [collectionsQuery.data]);

  const themeIds = useMemo(() => new Set(themes.map((t) => t.id)), [themes]);
  const collectionIds = useMemo(() => new Set(collections.map((c) => c.id)), [collections]);

  // ── Live (edit) mode state ────────────────────────────────────────────────
  const [liveSelectedThemes, setLiveSelectedThemes] = useState<SelectedTag[]>([]);
  const [liveSelectedCollections, setLiveSelectedCollections] = useState<SelectedTag[]>([]);

  // Seed from pkg ONCE per package (after the master list loads). Without this
  // guard the seed would re-run on every list refetch - e.g. right after creating
  // a new tag inline - and wipe the just-added (unsaved) selection.
  const seededThemes = useRef(false);
  const seededCollections = useRef(false);
  const lastPkgId = useRef<string | null>(null);

  useEffect(() => {
    if (isControlled) return;
    const pid = pkg?.id ?? null;
    if (pid !== lastPkgId.current) {
      lastPkgId.current = pid;
      seededThemes.current = false;
      seededCollections.current = false;
    }
  }, [isControlled, pkg]);

  useEffect(() => {
    if (isControlled || seededThemes.current) return;
    if (themes.length > 0 || themesQuery.isSuccess) {
      setLiveSelectedThemes(pkg ? seedTags(pkg.collections, themeIds) : []);
      seededThemes.current = true;
    }
  }, [isControlled, pkg, themeIds, themes.length, themesQuery.isSuccess]);

  useEffect(() => {
    if (isControlled || seededCollections.current) return;
    if (collections.length > 0 || collectionsQuery.isSuccess) {
      setLiveSelectedCollections(pkg ? seedTags(pkg.collections, collectionIds) : []);
      seededCollections.current = true;
    }
  }, [isControlled, pkg, collectionIds, collections.length, collectionsQuery.isSuccess]);

  // ── Controlled (create) mode state ───────────────────────────────────────
  // The draftValue is the combined id list. Split into themes vs collections
  // once the master lists have loaded (to get names + know which type each id is).
  const [draftSelectedThemes, setDraftSelectedThemes] = useState<SelectedTag[]>([]);
  const [draftSelectedCollections, setDraftSelectedCollections] = useState<SelectedTag[]>([]);

  useEffect(() => {
    if (!isControlled) return;
    const ids = draftValue ?? [];
    setDraftSelectedThemes(
      ids
        .filter((id) => themeIds.has(id))
        .map((id) => {
          const entry = themes.find((t) => t.id === id);
          return { marketingTagId: id, name: entry?.name ?? id };
        }),
    );
    setDraftSelectedCollections(
      ids
        .filter((id) => collectionIds.has(id))
        .map((id) => {
          const entry = collections.find((c) => c.id === id);
          return { marketingTagId: id, name: entry?.name ?? id };
        }),
    );
  }, [isControlled, draftValue, themes, collections, themeIds, collectionIds]);

  // Unified read surface
  const selectedThemes = isControlled ? draftSelectedThemes : liveSelectedThemes;
  const selectedCollections = isControlled ? draftSelectedCollections : liveSelectedCollections;

  // Helper to emit union after a change in controlled mode
  const emitDraftChange = (nextThemes: SelectedTag[], nextCollections: SelectedTag[]): void => {
    if (isControlled) {
      onDraftChange([
        ...nextThemes.map((t) => t.marketingTagId),
        ...nextCollections.map((c) => c.marketingTagId),
      ]);
    }
  };

  // ── Dirty detection (live mode only) ─────────────────────────────────────
  const currentIds = useMemo(
    () => (pkg ? pkg.collections.map((c) => c.marketingTagId) : []),
    [pkg],
  );
  const selectedIds = useMemo(
    () => [
      ...selectedThemes.map((t) => t.marketingTagId),
      ...selectedCollections.map((c) => c.marketingTagId),
    ],
    [selectedThemes, selectedCollections],
  );
  const dirty =
    !isControlled &&
    (selectedIds.length !== currentIds.length ||
      selectedIds.some((id) => !currentIds.includes(id)) ||
      currentIds.some((id) => !selectedIds.includes(id)));

  // ── Theme picker options ──────────────────────────────────────────────────
  const themeOptions = useMemo(
    () =>
      themes
        .filter((t) => !selectedThemes.some((s) => s.marketingTagId === t.id))
        .map((t) => ({ value: t.id, label: t.name })),
    [themes, selectedThemes],
  );

  // ── Collection picker options ─────────────────────────────────────────────
  const collectionOptions = useMemo(
    () =>
      collections
        .filter((c) => !selectedCollections.some((s) => s.marketingTagId === c.id))
        .map((c) => ({ value: c.id, label: c.name })),
    [collections, selectedCollections],
  );

  const addTheme = (id: string): void => {
    const entry = themes.find((t) => t.id === id);
    if (!entry) return;
    const next = isControlled
      ? selectedThemes.some((s) => s.marketingTagId === id)
        ? selectedThemes
        : [...selectedThemes, { marketingTagId: entry.id, name: entry.name }]
      : liveSelectedThemes.some((s) => s.marketingTagId === id)
        ? liveSelectedThemes
        : [...liveSelectedThemes, { marketingTagId: entry.id, name: entry.name }];

    if (isControlled) {
      setDraftSelectedThemes(next);
      emitDraftChange(next, selectedCollections);
    } else {
      setLiveSelectedThemes(next);
    }
  };

  const removeTheme = (id: string): void => {
    const next = selectedThemes.filter((s) => s.marketingTagId !== id);
    if (isControlled) {
      setDraftSelectedThemes(next);
      emitDraftChange(next, selectedCollections);
    } else {
      setLiveSelectedThemes(next);
    }
  };

  const addCollection = (id: string): void => {
    const entry = collections.find((c) => c.id === id);
    if (!entry) return;
    const next = isControlled
      ? selectedCollections.some((s) => s.marketingTagId === id)
        ? selectedCollections
        : [...selectedCollections, { marketingTagId: entry.id, name: entry.name }]
      : liveSelectedCollections.some((s) => s.marketingTagId === id)
        ? liveSelectedCollections
        : [...liveSelectedCollections, { marketingTagId: entry.id, name: entry.name }];

    if (isControlled) {
      setDraftSelectedCollections(next);
      emitDraftChange(selectedThemes, next);
    } else {
      setLiveSelectedCollections(next);
    }
  };

  const removeCollection = (id: string): void => {
    const next = selectedCollections.filter((s) => s.marketingTagId !== id);
    if (isControlled) {
      setDraftSelectedCollections(next);
      emitDraftChange(selectedThemes, next);
    } else {
      setLiveSelectedCollections(next);
    }
  };

  // Create a brand-new theme/collection master tag inline, then select it.
  const createTheme = async (name: string): Promise<void> => {
    try {
      const tag = await createTag.mutateAsync({ type: 'theme', name });
      const item = { marketingTagId: tag.id, name: tag.name };
      if (isControlled) {
        const next = [...selectedThemes, item];
        setDraftSelectedThemes(next);
        emitDraftChange(next, selectedCollections);
      } else {
        setLiveSelectedThemes((cur) =>
          cur.some((s) => s.marketingTagId === item.marketingTagId) ? cur : [...cur, item],
        );
      }
    } catch {
      /* error surfaced by the create hook's toast */
    }
  };

  const createCollection = async (name: string): Promise<void> => {
    try {
      const tag = await createTag.mutateAsync({ type: 'collection', name });
      const item = { marketingTagId: tag.id, name: tag.name };
      if (isControlled) {
        const next = [...selectedCollections, item];
        setDraftSelectedCollections(next);
        emitDraftChange(selectedThemes, next);
      } else {
        setLiveSelectedCollections((cur) =>
          cur.some((s) => s.marketingTagId === item.marketingTagId) ? cur : [...cur, item],
        );
      }
    } catch {
      /* error surfaced by the create hook's toast */
    }
  };

  const save = (): void => {
    void set.mutateAsync({
      marketingTagIds: [
        ...selectedThemes.map((t) => t.marketingTagId),
        ...selectedCollections.map((c) => c.marketingTagId),
      ],
    });
  };

  const isError = themesQuery.isError || collectionsQuery.isError;
  const isLoading = themesQuery.isLoading || collectionsQuery.isLoading;

  return (
    <section className={`space-y-4 ${className ?? ''}`}>
      <div>
        <h3 className="text-sm font-semibold text-zinc-900">Themes &amp; Collections</h3>
        <p className="text-xs text-zinc-500">
          Website browse groups. Manage the tags themselves under Masters → Themes &amp;
          Collections.
        </p>
      </div>

      {isError && <UzAlert tone="destructive">Failed to load themes or collections.</UzAlert>}

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Themes picker */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">Themes</h4>

          {!themesQuery.isLoading && themes.length === 0 && !themesQuery.isError && (
            <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
              No theme tags exist yet. Create them under Masters → Themes &amp; Collections.
            </p>
          )}

          <UzCombobox
            placeholder="Add a theme…"
            searchPlaceholder="Search or type to create…"
            value=""
            onValueChange={addTheme}
            options={themeOptions}
            onCreate={(name) => void createTheme(name)}
            createLabel={(q) => `Create theme “${q}”`}
            existingLabels={themes.map((t) => t.name)}
            loading={themesQuery.isLoading}
            disabled={
              (isControlled ? false : set.isPending) || themesQuery.isLoading || createTag.isPending
            }
            emptyText="No matching themes"
          />

          {selectedThemes.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
              No themes selected yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {selectedThemes.map((s) => (
                <li
                  key={s.marketingTagId}
                  className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm"
                >
                  <span className="truncate">{s.name}</span>
                  <button
                    type="button"
                    onClick={() => removeTheme(s.marketingTagId)}
                    disabled={isControlled ? false : set.isPending}
                    className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                    aria-label="Remove theme"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Collections picker */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
            Collections
          </h4>

          {!collectionsQuery.isLoading && collections.length === 0 && !collectionsQuery.isError && (
            <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
              No collection tags exist yet. Create them under Masters → Themes &amp; Collections.
            </p>
          )}

          <UzCombobox
            placeholder="Add a collection…"
            searchPlaceholder="Search or type to create…"
            value=""
            onValueChange={addCollection}
            options={collectionOptions}
            onCreate={(name) => void createCollection(name)}
            createLabel={(q) => `Create collection “${q}”`}
            existingLabels={collections.map((c) => c.name)}
            loading={collectionsQuery.isLoading}
            disabled={
              (isControlled ? false : set.isPending) ||
              collectionsQuery.isLoading ||
              createTag.isPending
            }
            emptyText="No matching collections"
          />

          {selectedCollections.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4 text-center text-xs text-zinc-500">
              No collections selected yet.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {selectedCollections.map((s) => (
                <li
                  key={s.marketingTagId}
                  className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2 text-sm"
                >
                  <span className="truncate">{s.name}</span>
                  <button
                    type="button"
                    onClick={() => removeCollection(s.marketingTagId)}
                    disabled={isControlled ? false : set.isPending}
                    className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                    aria-label="Remove collection"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {!isControlled && (
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-xs text-zinc-400">
            {isLoading ? 'Loading…' : dirty ? 'Unsaved changes' : 'Saved'}
          </span>
          <UzButton size="sm" onClick={save} loading={set.isPending} disabled={!dirty || isLoading}>
            Save collections
          </UzButton>
        </div>
      )}
    </section>
  );
};
